import React from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { ArrowRight, Award, Globe, HeartHandshake, Eye, Leaf, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { categories, origins, values, testimonials } from '../mock';

export const Home = () => {
  const { t } = useTranslation(['common', 'products']);
  
  return (
    <div className="bg-[#F6F1EA]">
      {/* Hero Section */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden">
        <div
          className="absolute inset-0 z-0"
          style={{
            backgroundImage: 'url(https://customer-assets.emergentagent.com/job_miela-luxury/artifacts/3gossyp5_image.png)',
            backgroundSize: 'cover',
            backgroundPosition: 'center'
          }}
        >
          {/* Luxury warm overlay to match brand colors */}
          <div className="absolute inset-0 bg-gradient-to-b from-[#3A312A]/40 via-[#3A312A]/20 to-[#1E1B18]/60"></div>
          {/* Subtle warm tint overlay for luxury feel */}
          <div className="absolute inset-0 bg-[#B08D57]/5"></div>
        </div>
        
        <div className="relative z-10 max-w-4xl mx-auto text-center px-6">
          <h1 className="text-5xl md:text-7xl font-serif font-bold text-white mb-6 leading-tight">
            {t('hero.title')}<br />{t('hero.subtitle')}
          </h1>
          <p className="text-xl md:text-2xl text-white/90 mb-10 leading-relaxed max-w-3xl mx-auto">
            {t('hero.description')}
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/products">
              <Button
                size="lg"
                className="bg-[#B08D57] hover:bg-[#8A6A3B] text-white px-8 py-6 text-lg transition-all duration-300 transform hover:scale-105"
              >
                {t('hero.exploreProducts')}
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </Link>
            <Link to="/contact">
              <Button
                size="lg"
                variant="outline"
                className="bg-white/10 backdrop-blur-sm border-white text-white hover:bg-white hover:text-[#3A312A] px-8 py-6 text-lg transition-all duration-300"
              >
                {t('hero.becomePartner')}
              </Button>
            </Link>
          </div>
        </div>

        {/* Scroll Indicator */}
        <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border-2 border-white/50 rounded-full flex items-start justify-center p-2">
            <div className="w-1 h-3 bg-white/50 rounded-full"></div>
          </div>
        </div>
      </section>

      {/* Brand Philosophy */}
      <section className="py-24 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-serif font-bold text-[#3A312A] mb-6">
            {t('philosophy.title')}
          </h2>
          <p className="text-lg text-[#3A312A]/80 leading-relaxed">
            {t('philosophy.description')}
          </p>
        </div>
      </section>

      {/* Featured Categories */}
      <section className="py-20 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-serif font-bold text-[#3A312A] mb-4">
              {t('collections.title')}
            </h2>
            <p className="text-lg text-[#3A312A]/70">{t('collections.subtitle')}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {categories.map((category, index) => (
              <Card
                key={category.id}
                className="group cursor-pointer border-none shadow-lg overflow-hidden transition-all duration-500 hover:shadow-2xl transform hover:-translate-y-2"
              >
                <div className="relative h-80 overflow-hidden">
                  <img
                    src={category.image}
                    alt={category.name}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-6 text-white">
                    <p className="text-sm font-medium text-[#B08D57] mb-2 tracking-wider uppercase">{t(`products:categories.${category.id}.subtitle`)}</p>
                    <h3 className="text-2xl font-serif font-bold mb-2">{t(`products:categories.${category.id}.name`)}</h3>
                    <p className="text-sm text-white/90">{t(`products:categories.${category.id}.description`)}</p>
                  </div>
                </div>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Miela Maison */}
      <section className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-serif font-bold text-[#3A312A] mb-4">
              {t('why.title')}
            </h2>
            <p className="text-lg text-[#3A312A]/70">{t('why.subtitle')}</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {values.map((value, index) => {
              const IconComponent = {
                'heart-handshake': HeartHandshake,
                'award': Award,
                'globe': Globe,
                'eye': Eye,
                'leaf': Leaf,
                'sparkles': Sparkles
              }[value.icon];

              const translationKey = value.id; // ethicalSourcing, premiumQuality, etc.

              return (
                <Card key={value.id} className="border border-[#E8DDD0] hover:border-[#B08D57] transition-all duration-300 hover:shadow-lg">
                  <CardContent className="p-8">
                    <div className="w-14 h-14 bg-[#B08D57]/10 rounded-full flex items-center justify-center mb-6">
                      <IconComponent className="text-[#B08D57]" size={28} />
                    </div>
                    <h3 className="text-xl font-bold text-[#3A312A] mb-3">{t(`values.${translationKey}.title`)}</h3>
                    <p className="text-[#3A312A]/70 leading-relaxed">{t(`values.${translationKey}.description`)}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Origins Section */}
      <section className="py-24 px-6 bg-white">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-serif font-bold text-[#3A312A] mb-4">
              {t('origins.title')}
            </h2>
            <p className="text-lg text-[#3A312A]/70">{t('origins.subtitle')}</p>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {origins.map((origin) => (
              <div
                key={origin.country}
                className="group relative overflow-hidden rounded-lg shadow-xl cursor-pointer transition-all duration-500 hover:shadow-2xl"
              >
                <div className="relative h-96">
                  <img
                    src={origin.image}
                    alt={origin.country}
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent"></div>
                  <div className="absolute bottom-0 left-0 right-0 p-8 text-white">
                    <h3 className="text-3xl font-serif font-bold mb-4">{t(`common:origins.${origin.id}.name`)}</h3>
                    <p className="text-sm text-white/90 mb-4 leading-relaxed">{t(`common:origins.${origin.id}.description`)}</p>
                    <div className="flex flex-wrap gap-2">
                      {origin.products.map((product, idx) => (
                        <span
                          key={`${origin.id}-${idx}`}
                          className="px-3 py-1 bg-[#B08D57] text-white text-xs font-medium rounded-full"
                        >
                          {t(`common:origins.${origin.id}.products.${idx}`)}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* B2B Section */}
      <section className="py-24 px-6 bg-[#3A312A] text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-serif font-bold mb-6">
            {t('b2b.title')}
          </h2>
          <p className="text-xl text-white/90 mb-8 leading-relaxed">
            {t('b2b.description')}
          </p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mb-10">
            <div className="text-center">
              <p className="text-4xl font-bold text-[#B08D57] mb-2">50+</p>
              <p className="text-sm text-white/80">{t('b2b.partners')}</p>
            </div>
            <div className="text-center">
              <p className="text-4xl font-bold text-[#B08D57] mb-2">15</p>
              <p className="text-sm text-white/80">{t('b2b.countries')}</p>
            </div>
            <div className="text-center">
              <p className="text-4xl font-bold text-[#B08D57] mb-2">100%</p>
              <p className="text-sm text-white/80">{t('b2b.traceability')}</p>
            </div>
            <div className="text-center">
              <p className="text-4xl font-bold text-[#B08D57] mb-2">6+</p>
              <p className="text-sm text-white/80">{t('b2b.years')}</p>
            </div>
          </div>
          <Link to="/contact">
            <Button
              size="lg"
              className="bg-[#B08D57] hover:bg-[#8A6A3B] text-white px-8 py-6 text-lg transition-all duration-300"
            >
              {t('b2b.cta')}
              <ArrowRight className="ml-2" size={20} />
            </Button>
          </Link>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-serif font-bold text-[#3A312A] mb-4">
              {t('testimonials.title')}
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {testimonials.map((testimonial) => (
              <Card key={testimonial.id} className="border border-[#E8DDD0] hover:shadow-lg transition-shadow duration-300">
                <CardContent className="p-8">
                  <p className="text-[#3A312A]/80 leading-relaxed mb-6 italic">
                    "{testimonial.content}"
                  </p>
                  <div>
                    <p className="font-bold text-[#3A312A]">{testimonial.name}</p>
                    <p className="text-sm text-[#3A312A]/60">{testimonial.position}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section
        className="relative py-32 px-6"
        style={{
          backgroundImage: 'url(https://customer-assets.emergentagent.com/job_miela-luxury/artifacts/imzuilk3_image.png)',
          backgroundSize: 'cover',
          backgroundPosition: 'center'
        }}
      >
        <div className="absolute inset-0 bg-[#3A312A]/70"></div>
        <div className="relative z-10 max-w-3xl mx-auto text-center text-white">
          <h2 className="text-4xl md:text-5xl font-serif font-bold mb-6">
            {t('finalCta.title')}
          </h2>
          <p className="text-xl text-white/90 mb-10 leading-relaxed">
            {t('finalCta.description')}
          </p>
          <Link to="/contact">
            <Button
              size="lg"
              className="bg-[#B08D57] hover:bg-[#8A6A3B] text-white px-10 py-6 text-lg transition-all duration-300 transform hover:scale-105"
            >
              {t('finalCta.button')}
              <ArrowRight className="ml-2" size={20} />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
};